"use client";

import { useState, useRef, useEffect } from "react";
import { useInView } from "react-intersection-observer";

export const Testimonials = ({ background }: { background: boolean }) => {
  const [order, setOrder] = useState<number>(1);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.7,
  });
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const [isMedium, setIsMedium] = useState<boolean>(false);

  // Swipe Effect
  const [startX, setStartX] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const threshold = 85;

  useEffect(() => {
    setIsMedium(window.innerWidth >= 768);
    const handleResize = () => setIsMedium(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const translateValue = isMedium ? -664 * (order - 1) : -391 * (order - 1);

  // useEffect(() => {
  //   startInterval();
  //   return () => stopInterval();
  // }, [startInterval]);

  const startInterval = () => {
    stopInterval();
    intervalRef.current = setInterval(() => {
      setOrder((prev) => (prev < 5 ? prev + 1 : 1));
    }, 8000);
  };

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleIndicatorClick = (idx: number) => {
    setOrder(idx + 1);
    startInterval();
  };

  return (
    <div
      id="testimonial"
      ref={ref}
      className={`w-full h-fit flex items-center justify-center ${background ? "bg-natura-lighter-2" : "bg-transparent"} px-6 py-12 lg:px-0 lg:pt-28 lg:pb-14`}
    >
      <div className="container mx-auto flex flex-col gap-12 md:gap-16">
        {/* Title */}
        <div className="flex flex-col gap-1.5 w-full items-center">
          <h5 className="text-base md:text-xl text-natura-darker-1 font-serifText">
            Testimonials
          </h5>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serifText text-natura-primary text-center">
            Stories We Love to Share
          </h2>
        </div>

        {/* Testimonial */}
        <div className="flex flex-col lg:flex-row gap-4 md:gap-20 w-full items-center justify-center">
          {/* Image */}
          <div className="relative size-[250px] md:size-[358px] flex items-center justify-center">
            {/* Ring */}
            <div className="absolute size-full inset-0 rounded-full p-[10px] bg-gradient-to-br from-[#E9D6EC] via-[#A535B9] to-[#471650] z-30"></div>
            {/* Content */}
            <div
              className="relative w-[calc(100%-20px)] h-[calc(100%-20px)] rounded-full shadow-lg z-30 transition-all duration-500 ease-out bg-cover bg-center"
              style={{
                backgroundImage: `url("/testimonial/image_${order}.webp")`,
              }}
            ></div>

            {/* Bubble */}
            <div
              className={`absolute bottom-0 right-0 flex items-center justify-center z-40 transition-all duration-1000 ease-out
              ${inView ? "scale-100 opacity-100" : "scale-0"}
              `}
            >
              <svg
                className="size-14 md:size-[81px]"
                viewBox="0 0 81 81"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="40.5386"
                  cy="40.7247"
                  r="40.275"
                  fill="url(#paint0_linear_1056_3247)"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_1056_3247"
                    x1="0.071822"
                    y1="0.449706"
                    x2="81.0718"
                    y2="80.8068"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#FFBD58" />
                    <stop offset="1" stopColor="#D99329" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            {/* Bubble */}
            <div
              className={`absolute flex items-center justify-center transition-all duration-[800ms] ease-out
                ${inView ? "left-0 top-0" : "left-1/2 top-1/2"}
              `}
            >
              <svg
                className="size-20 md:size-32"
                viewBox="0 0 126 126"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="62.9483"
                  cy="62.65"
                  r="62.65"
                  fill="url(#paint0_linear_1056_3243)"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_1056_3243"
                    x1="1.62976e-06"
                    y1="-1.62376e-06"
                    x2="126"
                    y2="125"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#FFBD58" />
                    <stop offset="1" stopColor="#D99329" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>

          {/* Testimonial */}
          <div className="flex flex-col gap-12 max-w-[327px] md:max-w-[600px]">
            <div className="flex flex-col gap-6">
              <div className="size-10 flex items-center justify-center">
                <svg
                  className={`transition-all duration-1000 ease-out
                      ${inView ? "rotate-0" : "rotate-180"}
                    `}
                  width="44"
                  height="37"
                  viewBox="0 0 44 37"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M16.0965 0.822986C6.83336 6.77208 1.68716 13.6967 0.657915 21.5968C-0.944995 33.8948 10.0527 39.9141 15.6254 34.5062C21.198 29.0984 17.8581 22.2328 13.8232 20.3556C9.78823 18.4784 7.32051 19.1328 7.75107 16.6245C8.18163 14.1162 13.9216 7.16451 18.9653 3.9267C19.1438 3.77455 19.2576 3.56014 19.2837 3.32706C19.3097 3.09398 19.246 2.85973 19.1055 2.67193L17.8581 1.04934C17.3169 0.345681 16.7977 0.372744 16.0965 0.821756M40.4859 0.822986C31.2227 6.77208 26.0765 13.6967 25.0472 21.5968C23.4431 33.8948 34.4408 39.9141 40.0135 34.5062C45.5861 29.0984 42.2462 22.2328 38.2101 20.3556C34.1739 18.4784 31.7074 19.1328 32.1379 16.6245C32.5685 14.1162 38.3097 7.16451 43.3534 3.9267C43.5317 3.77438 43.6453 3.55989 43.6711 3.32681C43.6969 3.09374 43.633 2.85958 43.4924 2.67193L42.2462 1.04934C41.705 0.345681 41.1858 0.372744 40.4859 0.821756"
                    fill="#D99329"
                  />
                </svg>
              </div>
              <div 
                className="flex gap-16 items-start justify-start overflow-hidden"
                onMouseDown={(e) => {
                  setStartX(e.clientX);
                  setIsDragging(true);
                  stopInterval(); // stop autoplay saat mulai drag
                }}
                onMouseMove={(e) => {
                  if (!isDragging || startX === null) return;
                  const delta = e.clientX - startX;
                  if (Math.abs(delta) > threshold) {
                    if (delta > 0) {
                      setOrder((prev) => (prev > 1 ? prev - 1 : 5));
                    } else {
                      setOrder((prev) => (prev < 5 ? prev + 1 : 1));
                    }
                    setStartX(null); // reset
                    setIsDragging(false);
                    startInterval(); // restart autoplay
                  }
                }}
                onMouseUp={() => {
                  setIsDragging(false);
                  setStartX(null);
                }}
                onTouchStart={(e) => {
                  setStartX(e.touches[0].clientX);
                  setIsDragging(true);
                  stopInterval();
                }}
                onTouchMove={(e) => {
                  if (!isDragging || startX === null) return;
                  const delta = e.touches[0].clientX - startX;
                  if (Math.abs(delta) > threshold) {
                    if (delta > 0) {
                      setOrder((prev) => (prev > 1 ? prev - 1 : 5));
                    } else {
                      setOrder((prev) => (prev < 5 ? prev + 1 : 1));
                    }
                    setStartX(null);
                    setIsDragging(false);
                    startInterval();
                  }
                }}
                onTouchEnd={() => {
                  setIsDragging(false);
                  setStartX(null);
                }}
              >
                {data.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col gap-4 transition-all duration-1000 ease-out"
                    style={{
                      transform: `translateX(${translateValue}px)`,
                      transition: "transform 0.5s ease-in-out",
                    }}
                  >
                    <h3 className="text-3xl md:text-3xl leading-tight font-serifText text-natura-primary">
                      {item.title}
                    </h3>
                    <p className="text-base md:text-lg leading-normal font-nunito text-black w-[327px] md:w-[600px]">
                      {item.description}
                    </p>
                    <div className="flex flex-col">
                      <p className="text-base md:text-lg leading-normal font-nunito font-bold text-black">
                        {item.name}
                      </p>
                      <p className="text-base md:text-lg leading-normal italic font-nunito text-black">
                        {item.roleship}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Indicator */}
            <div className="flex gap-3">
              {[...Array(5)].map((_, idx) => (
                <div
                  key={idx}
                  onClick={() => handleIndicatorClick(idx)}
                  className={`h-1.5 md:h-2 rounded-full bg-natura-primary transition-all duration-300
                    ${order === idx + 1 ? "w-20 md:w-24 opacity-100" : "w-8 md:w-10 opacity-30 cursor-pointer"}  
                  `}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const data = [
  {
    title: "Klinik Gigi Ternyaman, Tim Sangat Ramah",
    description:
      "Klinik gigi ternyaman karena tempatnya bersih, luas, dan nggak kalah estetik. Tempatnya nyaman, tim ramah, mau ajak anak periksa ke sini bakalan betah juga. Proses selama dental spa dan scaling tuh nggak sakit sama sekali, before after-nya langsung terlihat gigi lebih cerah dan bersih.",
    name: "Risa intan Pramesti",
    roleship: "Beauty & Fashion Enthusiast",
  },
  {
    title: "Sudah 2 Tahun, Selalu Merasa Nyaman",
    description:
      "Sudah hampir 2 tahun kontrol setelah pemasangan behel pertama dan selalu nyaman setiap datang ke Natura. Fasilitas dan pelayanannya sangat bagus dan bisa bikin puas setiap kontrol. Dokter yang menangani juga sangat baik dan selalu ngasih edukasi kesehatan gigi. Bintang 5!",
    name: "Sadam Permana, S.H",
    roleship: "Social Media Influencer, 500K+ Followers",
  },
  {
    title: "Budget Planning yang Transparan",
    description:
      "Dengan teknologi modern dan anestesi di Natura, prosesnya hampir minim rasa sakit. Selain itu, di Natura, Perawatan Saluran Akar (PSA) dilengkapi budget planning yang transparan, tanpa biaya tersembunyi. Jadi nyaman untuk invetasi jangka panjang.",
    name: "Valenabelle Reimas",
    roleship: "Beauty Enthusiast",
  },
  {
    title: "Sekarang Jadi Lebih Percaya Diri",
    description:
      "Awal sebelum pemasangan gigi bawah saya berantakan setelah pemasangan damon behel dengan drg. Nin yang super baik dan sabar. Alhamdulillah gigi saya sekarang sudah rapi dan kalau senyum sekarang jadi nambah percaya diri tentunya.",
    name: "Dr. Clara Vidhia, ST., MT.",
    roleship: "Dosen, Universitas Surapati",
  },
  {
    title: "Tempatnya Asik dan Cozy Banget",
    description:
      "Gue seneng banget sama pelayanannya, mulai dari resepsionisnya, perawatnya sampai dokternya ramah banget. Tempatnya asik, cozy banget. Cara kerja dokternya teliti dan penjelasannya jelas banget. Pokoknya gue puas banget treatment di sini. Kalian datang juga, ya!",
    name: "Zidan Akmal",
    roleship: "Model, Wakil 2 Abang None Jakarta 2023",
  },
];
